import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * VendorLogin — dedicated sign-in page for vendors. Uses the same auth
 * endpoint as the buyer Login, but the copy + left panel is tailored for
 * vendors (lead delivery, wallet, KYC) so marketing and nav links can point
 * here with a clear vendor-specific destination.
 *
 * On successful sign-in we route non-vendor accounts to their role's
 * dashboard, so a buyer who lands on /vendor/login by accident still ends
 * up in the right place.
 */
export default function VendorLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next');

  const [form, setForm] = useState({
    email: localStorage.getItem('kanjusee_remember_email') || '',
    password: '',
  });
  const [remember, setRemember] = useState(!!localStorage.getItem('kanjusee_remember_email'));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await authAPI.login({ ...form, role: 'vendor' });
      const user = res.data.data.user;
      if (user.role !== 'vendor') {
        setError('Access denied. This login page is only for vendors.');
        return;
      }
      if (remember) localStorage.setItem('kanjusee_remember_email', form.email);
      else localStorage.removeItem('kanjusee_remember_email');
      login(res.data.data.token, user);

      if (nextPath && nextPath.startsWith('/vendor')) {
        navigate(nextPath);
      } else {
        navigate('/vendor/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed.');
    } finally { setLoading(false); }
  };

  return (
    <div className="flex-stack" style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      {/* Left — vendor-flavoured branding */}
      <div className="hide-mobile" style={{ flex: 1, background: 'var(--green-dk)', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '60px 50px', minWidth: 0 }}>
        <div>
          <span className="logo" style={{ color: '#fff', fontSize: 32 }}>Kanjusee</span>
          <span className="logo-dot" style={{ fontSize: 36 }}>.</span>
          <div style={{ display: 'inline-block', marginLeft: 12, padding: '3px 10px', background: 'var(--gold)', color: '#1A1A1A', borderRadius: 6, fontSize: 11, fontWeight: 800, letterSpacing: 1, verticalAlign: 'middle' }}>VENDOR</div>
        </div>
        <p style={{ fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: '#fff', marginTop: 24, lineHeight: 1.3 }}>
          Welcome back, vendor.<br />Your leads are waiting.
        </p>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginTop: 12, lineHeight: 1.7 }}>
          Sign in to review new demands, send competitive offers, and manage your wallet.
        </p>

        <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { icon: '📥', text: 'Fresh leads in your category' },
            { icon: '💼', text: 'Manage offers and negotiations' },
            { icon: '💳', text: 'Wallet, invoices, GST in one place' },
            { icon: '✅', text: 'Verified-vendor badge on every offer' },
          ].map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'rgba(255,255,255,0.75)', fontSize: 14 }}>
              <span style={{ fontSize: 20 }}>{f.icon}</span> {f.text}
            </div>
          ))}
        </div>
      </div>

      {/* Right — form */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 380 }}>
          <div className="show-mobile" style={{ textAlign: 'center', marginBottom: 24 }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <span className="logo" style={{ color: 'var(--green)', fontSize: 28 }}>Kanjusee</span>
              <span className="logo-dot" style={{ fontSize: 32 }}>.</span>
              <div style={{ display: 'inline-block', marginLeft: 8, padding: '2px 8px', background: 'var(--gold)', color: '#1A1A1A', borderRadius: 6, fontSize: 10, fontWeight: 800, letterSpacing: 1, verticalAlign: 'middle' }}>VENDOR</div>
            </Link>
          </div>
          <h2 className="heading-md" style={{ marginBottom: 4 }}>Vendor sign in</h2>
          <p className="text-muted text-sm" style={{ marginBottom: 28 }}>Use the credentials you created at sign-up.</p>

          {error && (
            <div style={{ background: 'var(--red-lt)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16, fontWeight: 500 }}>{error}</div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" required autoFocus value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@firm.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Your password" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: 'var(--green)', cursor: 'pointer' }} />
                Remember me
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <Link to="/forgot-password" style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600, textDecoration: 'none' }}>Forgot password?</Link>
                <Link to="/login" style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textDecoration: 'none' }}>Sign in as buyer →</Link>
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn btn-pri btn-lg btn-full">
              {loading ? 'Signing in...' : 'Sign In to Vendor Dashboard →'}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--muted)' }}>
            New to Kanjusee? <Link to="/vendor-signup" style={{ color: 'var(--green)', fontWeight: 700, textDecoration: 'none' }}>Create a vendor account</Link>
          </p>
          <p style={{ textAlign: 'center', marginTop: 8 }}>
            <Link to="/" style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>← Back to home</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
